import { Component, OnInit, Inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { Observable, throwError } from 'rxjs'
import { filter, switchMap, finalize, debounceTime, takeUntil, catchError } from 'rxjs/operators'

import { DeviceInstallation, LinkDeviceToSensorData } from '@shared/models/device.model'
import { DevicesService } from '@shared/modules/devices/devices.service'
import { DestroyService } from '@core/services/destroy.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { HttpErrorResponse } from '@angular/common/http'

@Component({
  selector: 'lin-link-device-to-sensor-dialog',
  templateUrl: './link-device-to-sensor-dialog.component.html',
  styleUrls: ['./link-device-to-sensor-dialog.component.scss'],
  providers: [DestroyService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LinkDeviceToSensorDialogComponent implements OnInit {
  form: FormGroup
  isSubmitting = false
  deviceInstallationData: DeviceInstallation
  errorMessage: string | null = null

  get isClientUser(): boolean {
    return this.authService.getCurrentUser().isClient()
  }

  constructor(
    private fb: FormBuilder,
    private devicesService: DevicesService,
    public dialogRef: MatDialogRef<LinkDeviceToSensorDialogComponent>,
    private readonly cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) private data: LinkDeviceToSensorData,
    private readonly authService: AuthService,
    private readonly destroyService: DestroyService,
  ) {}

  ngOnInit(): void {
    this.buildForm()
    this.getDeviceInstalationData()
  }

  handleValueChanges(data: DeviceInstallation, touchedCtrl: string): void {
    const ctrlForUpdate = touchedCtrl === 'imei' ? 'regNo' : 'imei'

    if (data?.deviceId) {
      this.deviceInstallationData = data
      this.form.patchValue(
        {
          [ctrlForUpdate]: ctrlForUpdate === 'imei' ? data.device.imei : data.vehicle.regNo,
        },
        { emitEvent: false, onlySelf: true },
      )
    } else {
      this.form.get(ctrlForUpdate).reset()
      this.deviceInstallationData = null
    }
  }

  installDevice(): void {
    if (!this.deviceInstallationData?.deviceId || this.isSubmitting) return

    this.isSubmitting = true
    this.errorMessage = null

    this.devicesService
      .linkDeviceToSensor(this.data.sensorId, { deviceId: this.deviceInstallationData.deviceId })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 400) {
            const errorDetail = error.error?.errors?.[0]?.detail?.sensor ?? error.message
            this.errorMessage = errorDetail || 'An unknown error occurred. Please try again.'
          } else {
            this.errorMessage = error.message || 'An unexpected error occurred.'
          }

          this.cdr.markForCheck()

          this.isSubmitting = false
          return throwError(() => error)
        }),
        finalize(() => {
          this.isSubmitting = false
        }),
      )
      .subscribe({
        next: () => this.dialogRef.close(true),
      })
  }

  private buildForm(): void {
    this.form = this.fb.group({
      imei: ['', [Validators.required, Validators.pattern('^[0-9-]*$')]],
      regNo: ['', Validators.required],
    })
  }

  private getDeviceInstalationData(): void {
    const observedCtrls = ['imei', 'regNo']

    observedCtrls.forEach((ctrlName) => {
      this.form.controls[ctrlName].valueChanges
        .pipe(
          debounceTime(200),
          filter((val: string) => val && val.length >= 3),
          switchMap((val: string) => {
            const deviceInstallationData: { [k: string]: Observable<DeviceInstallation> } = {
              imei: this.devicesService.getDeviceInstallation(val),
              regNo: this.devicesService.getDeviceInstallation(null, val),
            }
            return deviceInstallationData[ctrlName]
          }),
          takeUntil(this.destroyService),
        )
        .subscribe((data: DeviceInstallation) => {
          this.handleValueChanges(data, ctrlName)
          this.cdr.markForCheck()
        })
    })
  }
}
